import { motion } from "framer-motion";
import { ReactComponent as LeftCloudIcon } from "shared/assets/animation-icons/leftCloudIcon.svg";
import { ReactComponent as RightCloudIcon } from "shared/assets/animation-icons/rightCloudIcon.svg";
import { ReactComponent as CenteredCloudIcon } from "shared/assets/animation-icons/centeredCloudIcon.svg";
import { ReactComponent as SunIcon } from "shared/assets/animation-icons/sunIcon.svg";

export type CloudCoverage = "default" | "medium" | "heavy";
export type CenterElement = "sun" | "cloud";

export interface OutcomeIllustrationProps {
  testId: string;
  cloudCoverage?: CloudCoverage;
  centerElement?: CenterElement;
}

const CLOUD_TRANSITION_DURATION = 0.5;
const CENTER_ELEMENT_APPEAR_DELAY = CLOUD_TRANSITION_DURATION * 0.6;
const CLOUD_SCALE_BY_COVERAGE: Record<CloudCoverage, number> = {
  default: 1,
  medium: 1.2,
  heavy: 1.45,
};

const getIllustrationVariants = (cloudScale: number) => ({
  leftCloud: {
    initial: { x: -160, scale: cloudScale, opacity: 1 },
    animate: {
      x: 0,
      scale: cloudScale,
      opacity: 1,
      transition: { duration: CLOUD_TRANSITION_DURATION, ease: "easeOut" },
    },
  },
  rightCloud: {
    initial: { x: 160, scale: cloudScale, opacity: 1 },
    animate: {
      x: 0,
      scale: cloudScale,
      opacity: 1,
      transition: { duration: CLOUD_TRANSITION_DURATION, ease: "easeOut" },
    },
  },
  centerElement: {
    initial: { y: 24, scale: 0.7, opacity: 0 },
    animate: {
      y: 0,
      scale: 1,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut", delay: CENTER_ELEMENT_APPEAR_DELAY },
    },
  },
});

export const OutcomeIllustration = ({ testId, cloudCoverage = "default", centerElement = "sun" }: OutcomeIllustrationProps) => {
  const illustrationVariants = getIllustrationVariants(CLOUD_SCALE_BY_COVERAGE[cloudCoverage]);

  return (
    <div data-testid={testId} className="relative -mx-10 h-[260px] w-[calc(100%+80px)] overflow-hidden lg:mx-0 lg:w-full">
      <motion.div
        className={
          centerElement === "sun"
            ? "absolute inset-x-0 top-0 z-0 flex justify-center drop-shadow-[0_3px_20px_rgba(227,118,45,0.7)]"
            : "absolute inset-x-0 top-0 z-0 flex justify-center"
        }
        variants={illustrationVariants.centerElement}
        initial="initial"
        animate="animate"
      >
        {centerElement === "sun" ? <SunIcon /> : <CenteredCloudIcon />}
      </motion.div>
      <motion.div
        className="absolute bottom-0 left-0 z-20 origin-bottom-left"
        variants={illustrationVariants.leftCloud}
        initial="initial"
        animate="animate"
      >
        <LeftCloudIcon />
      </motion.div>
      <motion.div
        className="absolute bottom-0 right-0 z-10 origin-bottom-right"
        variants={illustrationVariants.rightCloud}
        initial="initial"
        animate="animate"
      >
        <RightCloudIcon />
      </motion.div>
    </div>
  );
};
